import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--bg);
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const Label = styled.label`
  display: block;
  color: var(--muted);
  font-size: 0.9rem;
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  min-height: 120px;
`;

const ButtonBar = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

const Primary = styled.button`
  padding: 10px 14px;
  border-radius: 20px;
  border: none;
  background: var(--accent);
  color: var(--accent-contrast);
  font-weight: 700;
  cursor: pointer;
`;

const Ghost = styled.button`
  padding: 10px 14px;
  border-radius: 20px;
  border: 1px solid var(--accent);
  background: transparent;
  color: var(--accent);
  font-weight: 700;
  cursor: pointer;
`;

export default function ProductEditor({ token, product, onChange }) {
  const isNew = !product;
  const [slug, setSlug] = useState(product?.slug || '');
  const [name, setName] = useState(product?.name || '');
  const [price, setPrice] = useState(product?.price ?? '');
  const [image, setImage] = useState(product?.image || '');
  const [shortDescription, setShort] = useState(product?.shortDescription || '');
  const [longDescription, setLong] = useState(product?.longDescription || '');
  const [featured, setFeatured] = useState(!!product?.featured);
  const [featuredRank, setFeaturedRank] = useState(product?.featured_rank ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      setSlug(product.slug || '');
      setName(product.name || '');
      setPrice(product.price ?? '');
      setImage(product.image || '');
      setShort(product.shortDescription || '');
      setLong(product.longDescription || '');
      setFeatured(!!product.featured);
      setFeaturedRank(product.featured_rank ?? '');
    }
  }, [product?.id]);

  const body = () => ({
    slug: slug || undefined,
    name: name || undefined,
    price: price === '' ? null : Number(price),
    image: image || undefined,
    shortDescription,
    longDescription,
    featured: !!featured,
    featured_rank: featuredRank === '' ? null : Number(featuredRank),
  });

  const create = async () => {
    if (!slug || !name) return alert('Slug and name are required');
    try {
      setSaving(true);
      const res = await fetch('/api/admin/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(body()),
      });
      if (!res.ok) throw new Error('Create failed');
      onChange && onChange();
      setSlug(''); setName(''); setPrice(''); setImage(''); setShort(''); setLong(''); setFeatured(false); setFeaturedRank('');
    } catch (e) {
      alert(e.message || 'Failed to create product');
    } finally {
      setSaving(false);
    }
  };

  const update = async () => {
    try {
      setSaving(true);
      const res = await fetch(`/api/admin/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(body()),
      });
      if (!res.ok) throw new Error('Update failed');
      onChange && onChange();
    } catch (e) {
      alert(e.message || 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm('Delete this product?')) return;
    try {
      setSaving(true);
      const res = await fetch(`/api/admin/products/${product.id}`, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!res.ok) throw new Error('Delete failed');
      onChange && onChange();
    } catch (e) {
      alert(e.message || 'Failed to delete product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <Row>
        <div>
          <Label>Slug</Label>
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="e.g. classic-tee" />
        </div>
        <div>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Product name" />
        </div>
      </Row>
      <Row>
        <div>
          <Label>Price (USD)</Label>
          <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="19.99" />
        </div>
        <div>
          <Label>Image URL</Label>
          <Input value={image} onChange={(e) => setImage(e.target.value)} placeholder="/images/shirt.jpg" />
        </div>
      </Row>
      <Row>
        <div>
          <Label>Short Description</Label>
          <Input value={shortDescription} onChange={(e) => setShort(e.target.value)} placeholder="One-liner" />
        </div>
        <div>
          <Label>Featured Rank</Label>
          <Input type="number" value={featuredRank} onChange={(e) => setFeaturedRank(e.target.value)} placeholder="e.g. 1" />
        </div>
      </Row>
      <div>
        <Label>Long Description</Label>
        <Textarea value={longDescription} onChange={(e) => setLong(e.target.value)} placeholder="Details..." />
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', color: 'var(--text)' }}>
          <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} /> Featured
        </label>
      </div>
      <ButtonBar>
        {isNew ? (
          <Primary onClick={create} disabled={saving}>Create</Primary>
        ) : (
          <>
            <Primary onClick={update} disabled={saving}>Save</Primary>
            <Ghost onClick={remove} disabled={saving}>Delete</Ghost>
          </>
        )}
      </ButtonBar>
    </Wrapper>
  );
}
